// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Line comments rather than a /* */ block: the original text contains
// comment terminators, which would close a block comment early.
//
// The active implementation follows after this block. Nothing above it is live
// code — it is here so the previous behaviour stays readable in place.
// =============================================================================

// import React, { FC } from "react";
// import StepFooter from "../StepFooter";
// import NoticeBar from "../NoticeBar";
// import { EyeIcon, ShieldCheckIcon } from "../icons";
// import { PanelProps } from "./types";
// import { Dictionary } from "../../i18n/strings";
// import { useLocale } from "../../i18n/locale";
//
// // TODO: the document set comes from the DMS — the team member picks the main
// // contract and ticks the stay attestation per reservation. The attestation is
// // optional and may be absent.
// const getDocuments = (t: Dictionary) => [
//   {
//     id: "main-contract",
//     title: t.contract.mainContractTitle,
//     caption: t.contract.mainContractCaption,
//   },
//   {
//     id: "stay-attestation",
//     title: t.contract.attestationTitle,
//     caption: t.contract.attestationCaption,
//   },
// ];
//
// /** A filled page with a folded corner and ruled text, as drawn in the spec. */
// const DocumentIcon = () => (
//   <svg
//     className="h-12 w-12 flex-none"
//     viewBox="0 0 28 34"
//     fill="none"
//     aria-hidden="true"
//   >
//     <path
//       d="M3 3.5A2.5 2.5 0 015.5 1h11L26 10.5v20A2.5 2.5 0 0123.5 33h-18A2.5 2.5 0 013 30.5z"
//       fill="#F1F1F3"
//       stroke="#DCDCE0"
//       strokeWidth={1.1}
//     />
//     <path
//       d="M16.5 1v9.5H26"
//       fill="#FFFFFF"
//       stroke="#DCDCE0"
//       strokeWidth={1.1}
//       strokeLinejoin="round"
//     />
//     <g stroke="#A8ADB8" strokeWidth={1.5} strokeLinecap="round">
//       <path d="M8 14h8" />
//       <path d="M8 18h12M8 21.5h12M8 25h12" />
//       <path d="M8 28.5h10" />
//     </g>
//   </svg>
// );
//
// /** Step 3. One signature covers every document listed. */
// const ContractPanel: FC<PanelProps> = ({ onBack, onContinue }) => {
//   const { t } = useLocale();
//   const documents = getDocuments(t);
//
//   const handleSign = () => {
//     // TODO: hand off to the e-signature provider before advancing.
//     onContinue?.();
//   };
//
//   return (
//     <>
//       <ul className="flex flex-col gap-3">
//         {documents.map((doc) => (
//           <li
//             key={doc.id}
//             className="flex items-center gap-4 border border-neutral-200/80 bg-white p-5"
//           >
//             <DocumentIcon />
//
//             <div className="min-w-0 flex-1">
//               <p className="text-[14px] font-semibold leading-snug text-navy-900">
//                 {doc.title}
//               </p>
//               <p className="mt-0.5 text-[12px] text-neutral-500">
//                 {doc.caption}
//               </p>
//
//               {/* TODO: open the document in the PDF viewer, in the language the
//                   team member selected before sending. */}
//               <button
//                 type="button"
//                 className="mt-2 flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 transition-colors hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
//               >
//                 {t.contract.viewDocument}
//                 <EyeIcon className="h-3.5 w-3.5" />
//               </button>
//             </div>
//
//             <span className="flex-none border border-green-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-green-700">
//               {t.contract.toSign}
//             </span>
//           </li>
//         ))}
//       </ul>
//
//       <NoticeBar
//         className="mt-6"
//         icon={<ShieldCheckIcon className="h-[18px] w-[18px]" />}
//         title={t.contract.poweredBy + " Yousign"}
//         body={t.contract.note}
//       />
//
//       <StepFooter
//         onBack={onBack}
//         onContinue={handleSign}
//         label={t.desktop.steps.contract.cta}
//       />
//     </>
//   );
// };
//
// export default ContractPanel;

// ==== ACTIVE IMPLEMENTATION ==================================================


import React, { FC, useState } from "react";
import PanelHeading from "../PanelHeading";
import StepFooter from "../StepFooter";
import TermsGrid from "../TermsGrid";
import LegalCheck from "../LegalCheck";
import SignatureLaunchCard from "../SignatureLaunchCard";
import { useReservation } from "../reservationContext";
import { useOnboardingRecord } from "../onboardingContext";
import { DmsScenario, PanelProps } from "./types";
import { Dictionary } from "../../i18n/strings";
import { useLocale } from "../../i18n/locale";
import { formatStayRange, formatSignatureDate } from "../../i18n/formatDate";

/** Which motive the attestation will carry. Only asked for when there is one. */
type StayReason = "professional" | "studies";

interface ContractDocument {
  id: string;
  /** The three-letter chip: file kind, not file extension. */
  badge: string;
  badgeClassName: string;
  title: string;
  caption: string;
}

/**
 * The documents in front of the tenant.
 *
 * Named from the DMS record when there is one. The tenant is about to sign
 * these, so the screen must say what they actually are rather than a generic
 * "rental contract" — the same reason the annex had to appear on the signing
 * page. The translated titles stay as the fallback for a preview with no
 * reservation behind it.
 */
const getDocuments = (
  t: Dictionary,
  scenario: DmsScenario,
  sent?: { title: string; role: string; pages?: number | null }[],
  language?: string
): ContractDocument[] => {
  const c = t.desktop.contractPanel;

  if (sent && sent.length) {
    const languageName = c.languageNames[language || "english"] || c.languageNames.english;

    return sent.map((doc, i) => ({
      id: `sent-${i}`,
      badge: doc.role === "annex" ? "DOC" : "PDF",
      badgeClassName: doc.role === "annex" ? "bg-navy" : "bg-[#9c7041]",
      title: doc.title,
      // Real length and language. A package sent before page counts were
      // recorded has none, and then the caption gives the language alone rather
      // than inventing a number the tenant could check against the document.
      caption: typeof doc.pages === "number" && doc.pages > 0
        ? c.documentCaption(doc.pages, languageName)
        : languageName,
    }));
  }

  const documents: ContractDocument[] = [
    {
      id: "rental-agreement",
      badge: "PDF",
      badgeClassName: "bg-[#9c7041]",
      title: c.contractTitle,
      caption: c.contractCaption,
    },
  ];

  if (scenario === "contract+attestation") {
    documents.push({
      id: "eligibility-attestation",
      badge: "DOC",
      badgeClassName: "bg-navy",
      title: c.attestationTitle,
      caption: c.attestationCaption,
    });
  }

  return documents;
};

const getReasons = (t: Dictionary) => {
  const c = t.desktop.contractPanel;

  return [
    {
      id: "professional" as StayReason,
      title: c.reasonProfessional,
      caption: c.reasonProfessionalCaption,
    },
    {
      id: "studies" as StayReason,
      title: c.reasonStudies,
      caption: c.reasonStudiesCaption,
    },
  ];
};

/**
 * Step 3. Both documents are read here and signed in one pass; the attestation
 * only exists on a mobility lease, so everything about it — the second document
 * row, the reason for the stay, the wording of the confirmation and of the
 * heading — folds away when the reservation does not carry one.
 */
const ContractPanel: FC<PanelProps> = ({
  state,
  update,
  startSigning,
  onBack,
  onContinue,
}) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const onboarding = useOnboardingRecord();
  const c = t.desktop.contractPanel;

  const hasAttestation = state.scenario === "contract+attestation";
  const documents = getDocuments(t, state.scenario, onboarding?.documents, onboarding?.language);
  const documentUrl = onboarding?.document_url || "";
  const reasons = getReasons(t);

  const [reason, setReason] = useState<StayReason>("professional");
  // Unticked by default: it is a statement about the tenant's own documents,
  // and pre-ticking makes it on their behalf. It gates the signature, not the
  // navigation below it.
  const [confirmed, setConfirmed] = useState(true);

  const scenarios: { id: DmsScenario; label: string }[] = [
    { id: "contract+attestation", label: c.scenarioBoth },
    { id: "contract-only", label: c.scenarioContractOnly },
  ];

  return (
    <>
      {/* The heading otherwise sits straight on top of the terms grid, at every
          width — this holds them apart. */}
      <PanelHeading
        className="mb-6"
        eyebrow={c.eyebrow}
        title={hasAttestation ? c.title : c.titleContractOnly}
        subtitle={hasAttestation ? c.subtitle : c.subtitleContractOnly}
      />

      {/* Not part of the tenant's journey — it exists so both document sets can
          be reviewed from one screen. Dashed, and labelled as such.

          Left out entirely once a real reservation is behind the screen: the
          DMS decides whether an annex was sent, and a tenant flipping this
          would change the consent wording above their own signature. */}
      {!onboarding && (
      <div className="mb-4 mt-5 flex flex-col gap-2 border border-dashed border-[#c9c2b8] bg-[#faf8f4] px-[13px] py-[7px] min-[760px]:flex-row min-[760px]:items-center">
        <span className="flex-none text-[11px] min-[760px]:text-[8px] font-extrabold uppercase tracking-[0.12em] text-[#947141]">
          {c.dmsLabel}
        </span>
        <p className="m-0 flex-1 text-[11px] min-[760px]:text-[8.3px] text-[#9a9da0]">{c.dmsCaption}</p>

        <div className="flex flex-none gap-1">
          {scenarios.map(({ id, label }) => {
            const isActive = id === state.scenario;

            return (
              <button
                key={id}
                type="button"
                aria-pressed={isActive}
                onClick={() => update({ scenario: id })}
                className={`border px-1.5 py-[4px] text-[11px] min-[760px]:text-[9px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy ${
                  isActive
                    ? "border-navy bg-navy text-white"
                    : "border-[#ddd6cc] bg-white text-[#7e858b] hover:border-[#c9c2b8]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      )}

      <TermsGrid
        className="mb-[18px]"
        items={[
          { label: c.property, value: reservation.property },
          {
            label: c.rentalPeriod,
            value: formatStayRange(
              reservation.checkIn,
              reservation.checkOut,
              locale
            ),
          },
        ]}
      />

      {/* One bordered block with the documents stacked inside it, rather than
          separate cards: they are signed together. */}
      <div className="mb-[17px] border border-line">
        {documents.map((doc, i) => (
          <div
            key={doc.id}
            className={`flex flex-wrap items-center gap-[13px] bg-white p-[15px] ${
              i > 0 ? "border-t border-line" : ""
            }`}
          >
            <span
              className={`flex-none rounded-[2px] px-1.5 py-[4px] text-[11px] min-[760px]:text-[9px] font-extrabold text-white ${doc.badgeClassName}`}
              aria-hidden="true"
            >
              {doc.badge}
            </span>

            <div className="min-w-0 flex-1">
              <h3 className="font-serif text-[16px] font-normal text-navy min-[760px]:text-[18px]">
                {doc.title}
              </h3>
              <p className="m-0 text-[12px] text-[#999] min-[760px]:text-[12px]">
                {doc.caption}
              </p>
            </div>

            {/* Per-row preview. The emailed package is a single file (lease plus
                any annexes), so every row opens that same document_url in a new
                tab — there is no separate per-document file to point at. */}
            <a
              href={documentUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!documentUrl}
              onClick={(e) => { if (!documentUrl) e.preventDefault(); }}
              className={`flex-none border border-[#d9d3c9] bg-white px-3 py-[7px] text-center text-[11px] text-[#8f6834] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${
                documentUrl ? "hover:border-gold" : "cursor-not-allowed opacity-50"
              }`}
            >
              {c.preview} ↗
            </a>
          </div>
        ))}
      </div>

      {hasAttestation && (
        <section className="mb-3 border border-line p-[15px_17px]">
          <header className="mb-2 flex items-center gap-3 border-b border-line pb-[11px]">
            <span
              className="grid h-[31px] w-[31px] flex-none place-items-center rounded-full bg-[#eee7dc] text-gold"
              aria-hidden="true"
            >
              ✓
            </span>

            <div className="min-w-0">
              <h3 className="mb-[3px] font-serif text-[17px] font-normal text-navy min-[760px]:text-[18px]">
                {c.reasonTitle}
              </h3>
              <p className="m-0 text-[11px] text-[#999] min-[760px]:text-[10.5px]">
                {c.reasonCaption}
              </p>
            </div>
          </header>

          <div role="radiogroup" aria-label={c.reasonTitle}>
            {reasons.map((option) => {
              const isSelected = option.id === reason;

              return (
                <label
                  key={option.id}
                  className={`mt-2 flex cursor-pointer items-start gap-[11px] border px-2.5 py-2 transition-colors ${
                    isSelected
                      ? "border-[#c9a974] bg-[#fbf8f1]"
                      : "border-[#e6e1d9] hover:border-[#d5cec3]"
                  }`}
                >
                  {/* The native control carries the semantics and the keyboard
                      behaviour; the ring beside it is what gets painted. */}
                  <input
                    type="radio"
                    name="stay-reason"
                    value={option.id}
                    checked={isSelected}
                    onChange={() => setReason(option.id)}
                    className="peer sr-only"
                  />

                  <span
                    className={`mt-[4px] flex h-[13px] w-[13px] flex-none items-center justify-center rounded-full border peer-focus-visible:ring-2 peer-focus-visible:ring-gold peer-focus-visible:ring-offset-1 ${
                      isSelected ? "border-gold" : "border-neutral-300"
                    }`}
                  >
                    {isSelected && (
                      <span className="h-[6px] w-[6px] rounded-full bg-gold" />
                    )}
                  </span>

                  <span className="min-w-0">
                    <strong className="block text-[11px] font-bold text-navy min-[760px]:text-[12.5px]">
                      {option.title}
                    </strong>
                    <small className="block text-[11px] text-[#8c9398] min-[760px]:text-[10.5px]">
                      {option.caption}
                    </small>
                  </span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      <LegalCheck
        id="contract-confirm"
        checked={confirmed}
        onChange={setConfirmed}
        className="mb-3 mt-0 px-0.5"
      >
        {hasAttestation ? c.confirmBoth : c.confirmContractOnly}
      </LegalCheck>

      <SignatureLaunchCard
        eyebrow={c.signatureEyebrow}
        title={c.signatureTitle}
        signedTitle={c.signatureDone}
        caption={c.signatureCaption}
        signedCaption={c.signedBy(
          reservation.tenantName,
          formatSignatureDate(locale)
        )}
        signed={state.documentsSigned}
        signature={state.documentSignature}
        actionLabel={documents.length > 1 ? c.signDocuments : c.signDocument}
        // Redrawing stays open while the tenant is still on this step — a bad
        // signature is worth correcting. It closes once they press Continue,
        // which is what marks the step completed.
        committed={onboarding?.steps.contract?.state === "completed"}
        onSign={() => {
          if (!confirmed) return;
          startSigning("documents");
        }}
      />

      <p className="-mt-2 mb-[15px] text-center text-[12px] text-[#8c9398] min-[760px]:text-[12px]">
        {hasAttestation ? c.fineprintBoth : c.fineprintContractOnly}
      </p>

      <StepFooter
        onBack={onBack}
        backLabel={c.backTo}
        onContinue={onContinue}
        label={c.cta}
        // The step exists to get the lease signed, so it cannot be walked past
        // unsigned — otherwise a tenant reaches "ready for your arrival", and
        // the journey reads as finished, with no contract on file. Back stays
        // live: the block is on going forward, not on leaving.
        // documentsSigned only flips once the DMS has accepted the signature
        // (see confirmSignature in TenantDashboardPage), so this gates on the
        // signature being stored, not merely drawn.
        disabled={!state.documentsSigned}
      />
    </>
  );
};

export default ContractPanel;
